import clsx from "clsx";
import { ErrorBoundary } from "react-error-boundary";
import { Link, useNavigate } from "react-router-dom";
import { ClassificationIcon, Grid } from "../../../../../../components";
import Typography from "../../../../../../components/typography/Typography";
import { FileIndicator, FileIndicators } from "../../../../@types";
import StaticAnalysisInsightsIndicators from "./StaticAnalysisInsightsIndicators";

import { SECTIONS } from "../../../../consts";
import commonStyles from "../../../../containers/report_summary/report-summary.module.scss";
import { useLocalSampleSummaryParams } from "../../../../hooks";
import { pathToSS } from "../../../../util";
import { SUMMARY_INDICATORS_ID } from "../../../summary/SummaryIndicators";
import styles from "./static-analysis-insights.module.scss";

const StaticAnalysisInsightsErrorComponent = () => (
  <span className={commonStyles.error_msg}>
    Error loading static analysis insights
  </span>
);

const StaticAnalysisInsightsNoDataComponent = () => (
  <span
    className={commonStyles.error_msg}
    data-testid="report_summary_static_analysis_insights_empty_grid"
  >
    No available static analysis insights
  </span>
);

const staticAnalysisInsightsGridSchema = (sourceId?: number) => ({
  fields: [
    {
      name: "indicator",
      displayTitle: "Indicator",
      sortable: false,
      component: (indicator: FileIndicator) => (
        <StaticAnalysisInsightsIndicators
          fileIndicators={indicator}
          sourceId={sourceId}
        />
      ),
      style: {
        width: "70%"
      }
    },
    {
      name: "file",
      displayTitle: "File",
      sortable: false,
      component: (indicator: FileIndicator) => (
        <Link
          to={pathToSS(
            indicator.sha1,
            sourceId,
            SECTIONS.TI_CORE.INDICATORS,
            indicator.relationship_id
          )}
          className={styles.grid_element_with_classification}
          data-testid={`report_summary_static_analysis_grid_file_link_${indicator.sha1}`}
        >
          <ClassificationIcon
            classification={indicator.classification}
            classificationStyle={{
              flexShrink: 0
            }}
          />
          <Typography
            variant={"body1"}
            className={`${styles.item_link} overflow-ellipsis`}
          >
            {indicator.filename}
          </Typography>
        </Link>
      ),
      style: {
        width: "30%"
      }
    }
  ]
});

interface Props {
  indicators?: FileIndicators;
  isLoading?: boolean;
}

const StaticAnalysisInsightsGrid = ({ indicators, isLoading }: Props) => {
  const navigate = useNavigate();
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();

  const pathToHWCT = pathToSS(
    hash,
    sourceId,
    SECTIONS.FILE_ANALYSIS.HOW_WE_CAUGHT_THIS,
    relationshipId
  );

  const handleMoreFilesClick = () => {
    navigate(pathToHWCT, {
      state: { scrollTo: SUMMARY_INDICATORS_ID }
    });
  };

  return (
    <ErrorBoundary fallback={<StaticAnalysisInsightsErrorComponent />}>
      <div className={styles.indicators_grid}>
        <Grid
          schema={staticAnalysisInsightsGridSchema(sourceId)}
          data={indicators?.indicators?.slice(0, 2) || []}
          emptyGridMsg={<StaticAnalysisInsightsNoDataComponent />}
          showLoading={isLoading}
          hidePaging
        />
        {(indicators?.count ?? 0) > 2 && (
          <Typography variant={"body1"} className={styles.more_files}>
            <a
              className={clsx(commonStyles.link, "pdf-hide-in-pdf")}
              onClick={handleMoreFilesClick}
            >
              +{(indicators?.count ?? 0) - 2} More Files With Indicators
            </a>

            <a
              className={clsx(commonStyles.link, "pdf-show-in-pdf")}
              href={pathToHWCT}
              style={{ display: "none" }}
            >
              +{(indicators?.count ?? 0) - 2} More Files With Indicators
            </a>
          </Typography>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default StaticAnalysisInsightsGrid;
